import React, { useMemo } from 'react';
import { listFromApiResponse, wizardDefaultsForToolType } from '../../../utils/billToItemCatalog';
import { useGetProductTypesQuery } from '../../../redux/productTypeApi';

const TYPE_ORDER = ['showroom', 'restaurant', 'mart', 'other'];

const TYPE_VISUALS = {
  showroom: {
    accent: { bg: 'bg-red-50', text: 'text-red-500', border: 'border-red-200' },
    Icon: () => (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 10.5 5.5 6h13L21 10.5" />
        <path d="M4 10.5h16V20H4V10.5Z" />
        <path d="M9.5 20v-6h5v6" />
        <path d="M4 10.5h4.2a2.3 2.3 0 0 0 3.6 0h.4a2.3 2.3 0 0 0 3.6 0H20" />
      </svg>
    ),
  },
  restaurant: {
    accent: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    Icon: () => (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M6 10a6 6 0 0 1 12 0v.2c0 1.2-.3 2.3-.8 3.3L16 16H8l-1.2-2.5A7.3 7.3 0 0 1 6 10.2V10Z" />
        <path d="M8 16h8" />
        <path d="M9 18h6" />
        <path d="M12 4v2" />
      </svg>
    ),
  },
  mart: {
    accent: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
    Icon: () => (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="m12 3 7 4-7 4-7-4 7-4Z" />
        <path d="m5 7 7 4 7-4" />
        <path d="M5 7v8l7 4 7-4V7" />
        <path d="M12 11v8" />
      </svg>
    ),
  },
  other: {
    accent: { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200' },
    Icon: () => (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="4" y="4" width="16" height="16" rx="3" />
        <path d="M8 12h8" />
        <path d="M12 8v8" />
      </svg>
    ),
  },
};

function typeKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
}

function visualKeyForType(name) {
  const key = typeKey(name);
  if (TYPE_VISUALS[key]) return key;
  if (key.includes('showroom') || key.includes('sweet')) return 'showroom';
  if (key.includes('restaurant')) return 'restaurant';
  if (key.includes('mart') || key.includes('rm pm') || key === 'rm_pm') return 'mart';
  return 'other';
}

function visualForType(name) {
  return TYPE_VISUALS[visualKeyForType(name)] || TYPE_VISUALS.other;
}

function StepSelectToolType({ wizardData, onChange }) {
  const { data: typesRes, isLoading, isError } = useGetProductTypesQuery({
    status: true,
    per_page: 500,
  });

  const productTypes = useMemo(() => {
    const list = listFromApiResponse(typesRes)
      .filter((row) => row?.id != null && row.status !== false)
      .map((row) => ({
        id: String(row.id),
        name: row.name || `Type ${row.id}`,
      }));
    return list.sort((a, b) => {
      const ai = TYPE_ORDER.indexOf(visualKeyForType(a.name));
      const bi = TYPE_ORDER.indexOf(visualKeyForType(b.name));
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [typesRes]);

  const selectedId = String(wizardData.productTypeId || wizardData.toolType || '');
  const selectedName = String(wizardData.productTypeName || '').trim().toLowerCase();

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Step 1: Select Operating Platform</h3>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-gray-500">Loading platforms…</p>
      ) : isError ? (
        <p className="py-12 text-center text-sm text-red-600">Could not load product types.</p>
      ) : productTypes.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500">No product types found.</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {productTypes.map((type) => {
            const selected =
              selectedId === type.id ||
              (selectedName && typeKey(type.name) === typeKey(selectedName)) ||
              typeKey(type.name) === typeKey(selectedId);
            const visual = visualForType(type.name);
            const Icon = visual.Icon;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => onChange(wizardDefaultsForToolType(type))}
                className={`relative flex min-h-[250px] flex-col items-center rounded-2xl border bg-white px-6 py-12 text-center transition ${
                  selected
                    ? 'border-brand border-2 shadow-sm ring-1 ring-brand/15'
                    : 'border-red-100 border-2 shadow-sm'
                }`}
              >
                <span
                  className={`absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border ${
                    selected ? 'border-brand border-2' : 'border-red-200 border-2'
                  }`}
                >
                  {selected ? <span className="h-2.5 w-2.5 rounded-full bg-brand" /> : null}
                </span>
                <div
                  className={`flex h-20 w-20 items-center justify-center rounded-full ${visual.accent.bg} ${visual.accent.text} ${visual.accent.border} border-2`}
                >
                  <Icon />
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <p className="text-[15px] font-semibold leading-4 text-gray-900">
                    Build To - Ordering Tool
                  </p>
                  <p className="mt-1 text-[1.35rem] font-bold leading-8 text-gray-900">
                    {type.name}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

    </div>
  );
}

export default StepSelectToolType;
